import { S, hasGame } from '../core/game';
import { dayNum, monthOf } from './calendar';

/**
 * Daily weather, rolled deterministically per game day from Taipei's monthly
 * climate: hot summers with afternoon thunderstorms, plum rains (梅雨) in
 * May–June, typhoon season July–October, and cool fronts (冷氣團) in winter.
 * Rain falls inside a time window, so a rainy day isn't wet from dawn to dusk.
 */
export type WeatherKind = 'sunny' | 'cloudy' | 'rain' | 'storm' | 'cool' | 'typhoon';
export const WEATHER_KINDS: readonly WeatherKind[] = ['sunny', 'cloudy', 'rain', 'storm', 'cool', 'typhoon'];

export interface RainWindow {
  /** Game minutes since midnight. */
  from: number;
  to: number;
  /** 0..1: drizzle ≈ 0.3, rain ≈ 0.6, downpour = 1. */
  power: number;
}

export interface Weather {
  kind: WeatherKind;
  /** Current temperature (°C, rounded). */
  temp: number;
  /** Chance of rain for the day, in tens like the CWA forecast. */
  rainPct: number;
  /** Day high / low (°C). */
  hi: number;
  lo: number;
  rain: RainWindow | null;
  /** Lightning and thunder inside the rain window. */
  thunder: boolean;
  /** 0..1 wind (typhoons blow hard). */
  wind: number;
}

export const WEATHER_ICON: Record<WeatherKind, string> = {
  sunny: '☀️',
  cloudy: '⛅',
  rain: '🌧️',
  storm: '⛈️',
  cool: '🌬️',
  typhoon: '🌀',
};

interface Climate {
  /** Mean daily high / low. */
  hi: number;
  lo: number;
  /** Probabilities of each day type (the rest is sunny). */
  typhoon: number;
  storm: number;
  rain: number;
  cool: number;
  cloudy: number;
}

// Taipei, roughly (CWA 1991–2020 normals, simplified into day types).
const CLIMATE: Climate[] = [
  { hi: 19, lo: 13, typhoon: 0, storm: 0, rain: 0.3, cool: 0.3, cloudy: 0.25 }, // Jan
  { hi: 20, lo: 14, typhoon: 0, storm: 0, rain: 0.32, cool: 0.25, cloudy: 0.25 }, // Feb
  { hi: 22, lo: 15, typhoon: 0, storm: 0.03, rain: 0.32, cool: 0.15, cloudy: 0.25 }, // Mar
  { hi: 26, lo: 19, typhoon: 0, storm: 0.08, rain: 0.3, cool: 0.04, cloudy: 0.25 }, // Apr
  { hi: 29, lo: 22, typhoon: 0, storm: 0.12, rain: 0.45, cool: 0, cloudy: 0.2 }, // May (plum rain)
  { hi: 32, lo: 25, typhoon: 0.01, storm: 0.18, rain: 0.38, cool: 0, cloudy: 0.18 }, // Jun (plum rain)
  { hi: 34, lo: 26, typhoon: 0.04, storm: 0.3, rain: 0.08, cool: 0, cloudy: 0.15 }, // Jul
  { hi: 34, lo: 26, typhoon: 0.05, storm: 0.28, rain: 0.08, cool: 0, cloudy: 0.15 }, // Aug
  { hi: 32, lo: 25, typhoon: 0.045, storm: 0.18, rain: 0.14, cool: 0, cloudy: 0.22 }, // Sep
  { hi: 28, lo: 22, typhoon: 0.025, storm: 0.04, rain: 0.25, cool: 0.04, cloudy: 0.25 }, // Oct
  { hi: 25, lo: 19, typhoon: 0, storm: 0, rain: 0.3, cool: 0.15, cloudy: 0.25 }, // Nov
  { hi: 21, lo: 15, typhoon: 0, storm: 0, rain: 0.28, cool: 0.3, cloudy: 0.25 }, // Dec
];

/** Small deterministic PRNG (mulberry32) seeded from the day number. */
function rng(seed: number): () => number {
  let a = (seed ^ 0x9e3779b9) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
// Picked so the first weeks show a bit of everything: an afternoon storm on the
// first Sunday, rain in week 3 and a typhoon at the end of September.
const SALT = 237;

function rawKind(k: number): WeatherKind {
  const c = CLIMATE[monthOf(k) - 1];
  const r = rng(k * 7919 + SALT)();
  let acc = 0;
  for (const kind of ['typhoon', 'storm', 'rain', 'cool', 'cloudy'] as const) {
    acc += c[kind];
    if (r < acc) return kind;
  }
  return 'sunny';
}

/** The day type for game day `k`, with the forced overrides and the rules. */
export function kindFor(k: number): WeatherKind {
  const forced = hasGame() ? S().weather?.[k] : undefined;
  if (forced) return forced;
  // A calm first week: the tutorial days are never typhoons.
  if (k === 0) return 'sunny';
  const raw = rawKind(k);
  if (raw !== 'typhoon') return raw;
  // Never in the first week, and at most one typhoon in any six days: the day
  // after one brings the trailing rain bands instead.
  if (k < 3) return 'storm';
  for (let j = Math.max(3, k - 5); j < k; j++) if (rawKind(j) === 'typhoon' && kindFor(j) === 'typhoon') return 'storm';
  return 'typhoon';
}

const cache = new Map<string, Omit<Weather, 'temp'>>();

/** The full weather of game day `k` (temperature = the day high). */
export function weatherFor(k: number): Omit<Weather, 'temp'> {
  const kind = kindFor(k);
  const key = `${k}:${kind}:${rainNowFor?.k === k ? rainNowFor.from : ''}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const month = monthOf(k);
  const c = CLIMATE[month - 1];
  const r = rng(k * 104729 + SALT * 3);
  const jitter = (r() - 0.5) * 3;
  const drop = { sunny: 0.5, cloudy: -1, rain: -2.5, storm: -0.5, cool: -6, typhoon: -3 }[kind];
  const hi = Math.round(c.hi + jitter + drop);
  const lo = Math.round(Math.min(hi - 3, c.lo + jitter * 0.6 + drop * 0.7));
  const tens = (a: number, b: number) => Math.round((a + r() * (b - a)) / 10) * 10;
  const winter = month >= 11 || month <= 3;
  const plum = month === 5 || month === 6;
  let rain: RainWindow | null = null;
  let rainPct = 0;
  switch (kind) {
    case 'sunny':
      rainPct = tens(0, 10);
      break;
    case 'cloudy': {
      rainPct = tens(20, 40);
      // Sometimes a passing shower.
      if (r() < 0.3) {
        const from = 9 * 60 + Math.round(r() * 8 * 60);
        rain = { from, to: from + 40 + Math.round(r() * 40), power: 0.3 };
      }
      break;
    }
    case 'cool': {
      rainPct = tens(10, 30);
      if (r() < 0.35) rain = { from: 6 * 60, to: 10 * 60 + Math.round(r() * 120), power: 0.25 };
      break;
    }
    case 'rain': {
      rainPct = tens(70, 90);
      const len = plum ? 4 * 60 + r() * 4 * 60 : 150 + r() * 210;
      const from = 7 * 60 + Math.round(r() * 9 * 60);
      rain = { from, to: Math.round(from + len), power: winter ? 0.38 : plum ? 0.72 : 0.6 };
      break;
    }
    case 'storm': {
      // The summer afternoon thunderstorm (西北雨): hot morning, sudden downpour.
      rainPct = tens(50, 80);
      const from = 13 * 60 + 30 + Math.round(r() * 150);
      rain = { from, to: from + 70 + Math.round(r() * 80), power: 1 };
      break;
    }
    case 'typhoon': {
      rainPct = 100;
      rain = { from: 0, to: 15 * 60 + Math.round(r() * 180), power: 1 };
      break;
    }
  }
  if (rain && rainNowFor?.k === k) {
    const len = rain.to - rain.from;
    rain = { ...rain, from: rainNowFor.from, to: Math.min(24 * 60, rainNowFor.from + Math.max(len, 180)) };
  }
  const out = { kind, rainPct, hi, lo, rain, thunder: kind === 'storm' || kind === 'typhoon', wind: kind === 'typhoon' ? 1 : kind === 'storm' ? 0.45 : kind === 'cool' ? 0.35 : 0.1 };
  cache.set(key, out);
  return out;
}

/** Rain strength 0..1 of a window at a given minute (with soft starts and ends). */
export function rainAt(w: RainWindow | null, minutes: number): number {
  if (!w || minutes < w.from || minutes > w.to) return 0;
  const ramp = Math.min(1, (minutes - w.from) / 18, (w.to - minutes) / 25);
  return Math.max(0, ramp) * w.power;
}

/** Temperature at a minute of the day: coolest at dawn, warmest mid-afternoon, cooler in the rain. */
export function tempAt(k: number, minutes: number): number {
  const w = weatherFor(k);
  const h = minutes / 60;
  const curve = (1 - Math.cos(((h - 5) / 18) * Math.PI * 2)) / 2; // 0 at 5:00, 1 at 14:00
  const peak = h >= 5 && h <= 23 ? (h <= 14 ? curve : Math.max(0, 1 - (h - 14) / 12)) : 0.05;
  const wet = rainAt(w.rain, minutes) * 2.5;
  return Math.round(w.lo + (w.hi - w.lo) * peak - wet);
}

const now = () => (hasGame() ? S().time : 9 * 60);

/** Today's weather right now. */
export function weatherNow(): Weather {
  const k = dayNum();
  return { ...weatherFor(k), temp: tempAt(k, now()) };
}

/** Rain strength right now (0..1). Only outdoor maps should show or feel it. */
export function rainPower(): number {
  return rainAt(weatherFor(dayNum()).rain, now());
}

/** Is it raining right now (outdoors)? */
export function isRaining(): boolean {
  return rainPower() > 0.04;
}

/** Did it rain at any point today (counts as watering the garden)? */
export function rainedToday(): boolean {
  const w = weatherFor(dayNum()).rain;
  return !!w && now() >= w.from;
}

/** Wind right now (0..1): typhoons howl while they last, then calm down. */
export function windPower(): number {
  const w = weatherFor(dayNum());
  if (w.kind === 'typhoon') return w.rain && now() <= w.rain.to + 60 ? 1 : 0.4;
  return w.wind;
}

/** Is today a typhoon day, and is the typhoon still passing over? */
export function typhoonDay(k: number = dayNum()): boolean {
  return kindFor(k) === 'typhoon';
}
export function typhoonActive(): boolean {
  const w = weatherFor(dayNum());
  return w.kind === 'typhoon' && !!w.rain && now() < w.rain.to;
}
export function typhoonTomorrow(): boolean {
  return typhoonDay(dayNum() + 1);
}

/** Cloud cover 0..1 for the lighting (sunny days are clear, typhoons are dark). */
export function gloomNow(): number {
  const w = weatherFor(dayNum());
  const base = { sunny: 0, cloudy: 0.35, cool: 0.4, rain: 0.35, storm: 0.1, typhoon: 0.6 }[w.kind];
  // Storm clouds build up an hour before a downpour and clear an hour after.
  let build = 0;
  if (w.rain) {
    const t = now();
    if (t >= w.rain.from - 60 && t <= w.rain.to + 60) build = Math.min(1, (t - (w.rain.from - 60)) / 60, (w.rain.to + 60 - t) / 60);
  }
  return Math.min(1, Math.max(base, build * (0.45 + 0.45 * (w.rain?.power ?? 0))));
}

/** Debug: make a forced day's rain start right now instead of at its rolled time. */
let rainNowFor: { k: number; from: number } | null = null;

/** Force a day's weather (debug helpers and scripted events). `startNow` moves its rain window to now. */
export function forceWeather(k: number, kind: WeatherKind | null, startNow = false): void {
  const s = S();
  rainNowFor = startNow && kind ? { k, from: Math.max(0, s.time - 20) } : rainNowFor?.k === k ? null : rainNowFor;
  s.weather ??= {};
  if (kind) s.weather[k] = kind;
  else delete s.weather[k];
  // Keep only the recent overrides.
  for (const key of Object.keys(s.weather)) if (Number(key) < k - 7) delete s.weather[Number(key)];
}
